import { html } from 'lit-element';

import bodyScrollLock from '@elab/core/utils/scroll-lock';
import { LightLitElement } from '@elab/components';
import Breakpoints from '@elab/core/utils/Breakpoints';
import connect from '@elab/core/store/connect';
import { actions, selectors } from '@elab/core/store/ui';

/**
 * Component show/hide content by click on header
 *
 * @attr {boolean} [open] - reflected attribute, show currents status of accordion
 * @attr {boolean} [disable-scroll] - disable scroll when accordion is opened
 * @attr {boolean} [default-open] - default state of accordion
 * @attr {boolean} [active-xxl] - Visible on xxl screen
 * @attr {boolean} [active-xl] - Visible on xl screen
 * @attr {boolean} [active-lg] - Visible on lg screen
 * @attr {boolean} [active-md] - Visible on md screen
 * @attr {boolean} [active-sm] - Visible on sm screen
 * @attr {string} [arrow-open-classes] - style classes for opened arrow (should be defined in accordion.scss)
 * @attr {string} [arrow-close-classes] - style classes for closed arrow (should be defined in accordion.scss)
 * @attr {string} [add-class] - additional class for styles (should be defined in accordion.scss)
 * @attr {string} [group-name] - Multiple accordions group name (only one could be opened, others will be closed)
 * @attr {string} [name] - Name of accordion in group (should be used with [group-name])
 *
 * From redux
 * @param {object} windowSizeWidth - information about sreen width
 *
 * HTML Structure Example
 *
 *  <e2-filters-accordion
 *      active-md active-sm active-lg active-xl active-xxl
 *      default-open
 *      add-class="class-name"
 *      name="1"
 *      group-name="group"
 *      arrow-open-classes="product-information__arrow product-information__arrow--opened"
 *      arrow-close-classes="product-information__arrow"
 *  >
 *      <div v-slot="title">Title</div>
 *      <div v-slot="content">Content</div>
 *   </e2-filters-accordion>
 */

class FiltersAccordion extends LightLitElement {
    static get properties() {
        return {
            open: { type: Boolean, attribute: false },
            active: { type: Boolean, attribute: false },
            disableScroll: { type: Boolean, attribute: 'disable-scroll' },
            defaultOpen: { type: Boolean, attribute: 'default-open' },
            activeXxl: { type: Boolean, attribute: 'active-xxl' },
            activeXl: { type: Boolean, attribute: 'active-xl' },
            activeLg: { type: Boolean, attribute: 'active-lg' },
            activeMd: { type: Boolean, attribute: 'active-md' },
            activeSm: { type: Boolean, attribute: 'active-sm' },
            arrowOpenClasses: { type: String, attribute: 'arrow-open-classes' },
            arrowCloseClasses: {
                type: String,
                attribute: 'arrow-close-classes',
            },
            addClass: { type: String, attribute: 'add-class' },
            groupName: { type: String, attribute: 'group-name' },
            name: { type: String, attribute: 'name' },
            windowSizeWidth: { type: Number },
        };
    }

    constructor() {
        super();
        this.open = false;
        this.disableScroll = false;
        this.defaultOpen = false;
        this.active = false;
        this.activeXxl = false;
        this.activeXl = false;
        this.activeLg = false;
        this.activeMd = false;
        this.activeSm = false;
        this.windowSizeWidth = window.innerWidth;
        this.arrowOpenClasses = '';
        this.arrowCloseClasses = '';
        this.addClass = '';
        this.groupName = '';
        this.name = '';
    }

    handleClick() {
        if (this.active) {
            this.open = !this.open;
            this.toggleClass();
            if (this.groupName && this.name) {
                this.fireCloseOtherAccordionInGroup();
            }
        }
    }

    getContent() {
        return this.querySelector('[v-slot="content"]');
    }

    fixBackgroundScroll() {
        // Prevent page scrolling when a modal is open (considering iOS specifics)
        if (this.open && Breakpoints.mobileDevice) {
            document.body.style.top = `-${window.scrollY}px`;
            document.body.style.position = 'fixed';
            document.body.setAttribute('plp-filters-opened', 'true');
        } else {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.setAttribute('plp-filters-opened', 'false');
        }
    }

    fireCloseOtherAccordionInGroup() {
        this.dispatchEvent(new CustomEvent('showFiltersMenu'));
    }

    handlerCloseEvent() {
        if (this.groupName != null && this.name !== null) {
            document.addEventListener(`${this.groupName}-CLOSE_OTHERS`, e => {
                const isNotCurrentAccordion = e.detail !== this.name;
                if (this.open && isNotCurrentAccordion) {
                    this.open = !this.open;
                }
            });
        }
    }

    firstUpdated() {
        this.menuFiltersOpened();
    }

    afterConnect() {
        this.open = !this.defaultOpen;
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (_changedProperties.has('windowSizeWidth')) {
            this.active = !!this.isActive();
            this.menuFiltersOpened();
        }
        if (_changedProperties.has('open') && !this.open) {
            bodyScrollLock.disable();
        }
    }

    toggleClass() {
        if (this.lastElementChild && this.lastElementChild.slot === 'content') {
            const content = this.getContent();
            if (this.open) {
                content.style.height = `${content.scrollHeight}px`;
                this.lastElementChild.classList.remove('opacity-with-transition');
                if (this.disableScroll) {
                    bodyScrollLock.enable();
                }
            } else {
                content.style.height = '';
                this.lastElementChild.classList.add('opacity-with-transition');
                if (this.disableScroll) {
                    bodyScrollLock.disable();
                }
            }
        }
    }

    menuFiltersOpened() {
        if (this.open && Breakpoints.tabletDevice) {
            this.defaultOpen = true;
        }
    }

    render() {
        this.toggleClass();
        return html`
            <div class="accordion-container ${this.addClass}" @click=${this.handleClick}>
                <v-slot name="title"></v-slot>
                <i
                    style="${this.active ? '' : 'display: none;'}"
                    class="${this.open ? this.arrowOpenClasses : this.arrowCloseClasses}"
                ></i>
            </div>
            <div>
                <v-slot name="content"></v-slot>
            </div>
        `;
    }

    isActive() {
        return (
            (Breakpoints.extraWideDevice && this.activeXxl) ||
            (Breakpoints.wideDevice && this.activeXl) ||
            (Breakpoints.desktopDevice && this.activeLg) ||
            (Breakpoints.onlyTabletDevice && this.activeMd) ||
            (Breakpoints.onlyTabletDevice && this.activeMd) ||
            (Breakpoints.mobileDevice && this.activeSm)
        );
    }
}

export const mapStateToProps = state => ({
    windowSizeWidth: selectors.getWindowSize(state).width,
    open: selectors.getFiltersMenuDisplayed(state),
});

const mapDispatchToEvents = dispatch => ({
    showFiltersMenu: () => {
        dispatch(actions.showFiltersMenu());
    },
});

export default () =>
    customElements.define(
        'e2-filters-accordion',
        connect(mapStateToProps, mapDispatchToEvents)(FiltersAccordion),
    );
